import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SiteHeader, SiteFooter, AgLogo } from '../components/SiteLayout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { ArrowRight, Eye, EyeSlash } from '@phosphor-icons/react';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    const result = await register(name, email, password, company);
    if (result.success) navigate('/dashboard');
    else setError(result.error);
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F9FAFB]">
      <SiteHeader />
      <main className="flex-1 flex items-center justify-center py-12 px-6">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-black tracking-tighter text-[#0B2447] mb-2">Create Account</h1>
            <p className="text-[#4B5563]">Start your toxicological assessments</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-[#FDE8E8] border border-[#F8B4B4] rounded-sm">
              <p className="text-sm text-[#9B1C1C]" data-testid="register-error">{error}</p>
            </div>
          )}

          <div className="bg-white p-8 border border-gray-200 rounded-sm">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-semibold text-[#111827]">Full name</Label>
                <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Dr. Jane Smith" required data-testid="register-name-input" className="h-11 rounded-sm border-gray-200" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-semibold text-[#111827]">Work email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required data-testid="register-email-input" className="h-11 rounded-sm border-gray-200" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company" className="text-sm font-semibold text-[#111827]">Company <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Input id="company" type="text" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Acme Cosmetics Inc." data-testid="register-company-input" className="h-11 rounded-sm border-gray-200" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-semibold text-[#111827]">Password</Label>
                <div className="relative">
                  <Input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="register-password-input" className="h-11 rounded-sm border-gray-200 pr-12" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-semibold text-[#111827]">Confirm password</Label>
                <Input id="confirmPassword" type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" required data-testid="register-confirm-password-input" className="h-11 rounded-sm border-gray-200" />
              </div>
              <Button type="submit" disabled={loading} data-testid="register-submit-btn" className="w-full h-11 btn-primary flex items-center justify-center gap-2 mt-2">
                {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <>Create account <ArrowRight size={18} weight="bold" /></>}
              </Button>
            </form>
          </div>

          <p className="mt-6 text-center text-[#4B5563]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#2563EB] hover:text-[#1D4ED8] font-semibold" data-testid="login-link">Sign in</Link>
          </p>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
